import * as React from "react";
import { useTableKindId } from "../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../newcomponents/table/data/useTableItemQueryData";
import { Table } from "../../newcomponents/table/table";
import { TableNewItemButton } from "../../newcomponents/table/components/tableNewButton";
import { CustomTableCPLNQuery } from "../../newcomponents/table/components/TableCplnQuery";
import { NameDescriptionColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { LastModifiedColumn } from "../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";
import { Identity } from "../../schema/types/identity/entity";
import { linksOf } from "../../services/cpln";
import { TableActionsDropdown } from "../../newcomponents/table/components/tableActionsDropdown";
import { DeleteModal } from "../../components/modals/deleteModal";
import { GvcColumn } from "../../newcomponents/table/columns/wellKnown/gvcColumn";
import { Query } from "../../schema/types/query/query";
import { Checkbox } from "antd";
import { IdentityTableItem, STORAGE_KEY_IDENTITY_LIST_LIMIT_TO_CONTEXT_GVC } from "../../mst/kinds/identity.helpers";
import { useAppSelector } from "../../store/hooks";
import { selectGvc } from "../../store/contextSlice";
import { useGvcListUrlState } from "../../reactHooks/useGvcListUrlState";
import { useColumnsWithTagColumns } from "../../newcomponents/table/columns/tagColumns/useColumnsWithTagColumns";

export const IdentityList: React.FC = () => {
  const gvc = useAppSelector(selectGvc);

  const { limitToContextGVC, setLimitToContextGVC } = useGvcListUrlState({
    localStorageKey: STORAGE_KEY_IDENTITY_LIST_LIMIT_TO_CONTEXT_GVC,
  });

  const limitToContextGVCQuery: Query = React.useMemo(
    () => ({
      kind: "identity",
      spec: {
        match: "all",
        terms: [
          {
            rel: "gvc",
            op: "=",
            value: gvc,
          },
        ],
      },
    }),
    [gvc],
  );

  // Determine the query to use based on limitToContextGVC and gvc
  const query = React.useMemo(() => {
    // If limitToContextGVC is enabled and there's a gvc context, use the GVC query
    if (limitToContextGVC && gvc) {
      return limitToContextGVCQuery;
    }
    // Otherwise, no query (fetch all identities)
    return undefined;
  }, [limitToContextGVC, gvc, limitToContextGVCQuery]);

  const [selectedLinks, setSelectedLinks] = React.useState<string[]>([]);
  const [action, setAction] = React.useState<string>("");

  const { kind, id } = useTableKindId("identity", "identity-list-update2");
  const qData = useTableItemQueryData<Identity>(kind, { query: query });
  const columns = useColumnsWithTagColumns<IdentityTableItem>(
    [NameDescriptionColumn(), GvcColumn(), TagsColumn(), CreatedColumn(), LastModifiedColumn()],
    kind,
  );

  // Show the actual state of the checkbox (whether it's limiting to context GVC)
  const processedLimitToContextGVC = React.useMemo(() => {
    // If there's no GVC context, can't limit to it
    if (!gvc) return false;
    // If there's a custom user query, respect that (don't show as limited to context)
    if (qData.query) return false;
    // Otherwise, show the limitToContextGVC state
    return limitToContextGVC;
  }, [gvc, qData.query, limitToContextGVC]);

  const identityTableItems: IdentityTableItem[] = qData.visibleItems.map((item) => {
    const selfLink = linksOf(item).self!;
    const gvc = selfLink.split("/")[4] || "";
    return { ...item, selfLink, gvc };
  });

  return (
    <>
      <Table<IdentityTableItem>
        title={"Identities"}
        data={identityTableItems}
        selectKey="selfLink"
        selectMode="multi"
        selections={selectedLinks}
        onSelectionsChange={setSelectedLinks}
        tableId={id}
        headerRenderer={() => {
          return (
            <>
              <span data-guide="limit-by-context">
                <Checkbox
                  key={`${gvc || "all"}`}
                  disabled={!gvc || !!qData.query}
                  checked={processedLimitToContextGVC}
                  onChange={(e) => setLimitToContextGVC(e.target.checked)}
                >
                  Limit with Context GVC
                </Checkbox>
              </span>
              <TableNewItemButton kind={kind} />
              <TableActionsDropdown
                kind={kind}
                selectionLinks={selectedLinks}
                options={[
                  {
                    key: "delete",
                    label: "Delete",
                    disabled: selectedLinks.length < 1,
                    danger: true,
                    callback: () => setAction("delete"),
                  },
                ]}
              />
              <TableHeaderRefreshButton onClick={qData.refetch} />
              <CustomTableCPLNQuery
                tableId={id}
                kind={kind}
                onQuery={qData.setCplnQuery}
                isLoading={qData.isLoading}
              />
            </>
          );
        }}
        enableFilter
        isLoading={qData.isLoading}
        onSortChange={qData.setSort}
        onSearchChange={qData.setSearch}
        isLoadingMore={qData.isLoadingMore}
        hasMore={qData.hasMore}
        onLoadMore={qData.fetchNextPage}
        columns={columns}
      />
      {action === "delete" ? (
        <DeleteModal
          kind={kind}
          onCancel={() => setAction("")}
          onComplete={() => {
            setAction("");
            setSelectedLinks([]);
            qData.refetch();
          }}
          links={selectedLinks}
        />
      ) : null}
    </>
  );
};
